import { useMemo } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import adminService from '@/services/adminService';
import { getJobStatus, pollJob } from '@/services/documentService';

export const marketingKeys = {
  all: ['admin', 'marketing'],
  templates: () => [...marketingKeys.all, 'templates'],
  template: (id) => [...marketingKeys.all, 'template', id],
  campaigns: () => [...marketingKeys.all, 'campaigns'],
  campaign: (id) => [...marketingKeys.all, 'campaign', id],
  recipients: (id) => [...marketingKeys.all, 'recipients', id],
  suppression: (search) => [...marketingKeys.all, 'suppression', search ?? ''],
  analytics: (period, campaignIds) => [
    ...marketingKeys.all,
    'analytics',
    period ?? 'all_time',
    campaignIds?.length ? [...campaignIds].sort().join(',') : 'all',
  ],
  analyticsRecipients: (params) => [...marketingKeys.all, 'analytics-recipients', params ?? {}],
  analyticsMetrics: (period, campaignIds) => [
    ...marketingKeys.all,
    'analytics-metrics',
    period ?? 'all_time',
    campaignIds?.length ? [...campaignIds].sort().join(',') : 'all',
  ],
  contacts: (params) => [...marketingKeys.all, 'contacts', params ?? {}],
  contact: (id) => [...marketingKeys.all, 'contact', id],
  contactHistory: (id) => [...marketingKeys.all, 'contact-history', id],
  contactTimeline: (id) => [...marketingKeys.all, 'contact-timeline', id],
  contactFollowUps: (params) => [...marketingKeys.all, 'contact-follow-ups', params ?? {}],
  actionCenter: () => [...marketingKeys.all, 'action-center'],
  savedViews: () => [...marketingKeys.all, 'saved-views'],
  adminUsers: () => [...marketingKeys.all, 'admin-users'],
};

export function useMarketingTemplates() {
  return useQuery({
    queryKey: marketingKeys.templates(),
    queryFn: () => adminService.listMarketingTemplates(),
    staleTime: 60_000,
  });
}

export function useMarketingTemplate(templateId, enabled = true) {
  return useQuery({
    queryKey: marketingKeys.template(templateId),
    queryFn: () => adminService.getMarketingTemplate(templateId),
    enabled: Boolean(templateId) && enabled,
    staleTime: 30_000,
  });
}

export function useSaveMarketingTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, payload }) => adminService.saveMarketingTemplate(templateId, payload),
    onSuccess: (_, { templateId }) => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.templates() });
      queryClient.invalidateQueries({ queryKey: marketingKeys.template(templateId) });
    },
  });
}

export function useDeleteMarketingTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (templateId) => adminService.deleteMarketingTemplate(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.templates() });
    },
  });
}

export function useMarketingCampaigns() {
  return useQuery({
    queryKey: marketingKeys.campaigns(),
    queryFn: () => adminService.listMarketingCampaigns(),
    staleTime: 15_000,
  });
}

export function useMarketingCampaign(campaignId, options = {}) {
  return useQuery({
    queryKey: marketingKeys.campaign(campaignId),
    queryFn: () => adminService.getMarketingCampaign(campaignId),
    enabled: Boolean(campaignId),
    refetchInterval: options.poll ? 5000 : false,
    staleTime: 5_000,
  });
}

export function useMarketingNoActivityRecipients(campaignId, days = 5, enabled = true, options = {}) {
  return useQuery({
    queryKey: [...marketingKeys.recipients(campaignId), 'no-activity', days],
    queryFn: () => adminService.getMarketingNoActivityRecipients(campaignId, days),
    enabled: Boolean(campaignId) && enabled,
    refetchInterval: options.poll ? 5000 : false,
    staleTime: 5_000,
  });
}

export function useMarketingCampaignRecipients(campaignId, options = {}) {
  const engagementFilter = options.engagementFilter || undefined;
  const registerClickType = options.registerClickType || undefined;
  const notSentType = options.notSentType || undefined;
  const limit = options.limit ?? 200;
  return useQuery({
    queryKey: [
      ...marketingKeys.recipients(campaignId),
      engagementFilter ?? 'all',
      registerClickType ?? 'all',
      notSentType ?? 'all',
      limit,
      options.exclusiveStartKey ?? null,
    ],
    queryFn: () =>
      adminService.getMarketingCampaignRecipients(campaignId, {
        limit,
        exclusive_start_key: options.exclusiveStartKey,
        engagement_filter: engagementFilter,
        register_click_type: registerClickType,
        not_sent_type: notSentType,
      }),
    enabled: Boolean(campaignId),
    refetchInterval: options.poll ? 5000 : false,
    staleTime: 5_000,
  });
}

export function useDeleteMarketingCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (campaignId) => adminService.deleteMarketingCampaign(campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.campaigns() });
    },
  });
}

export function useCreateMarketingCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => adminService.createMarketingCampaign(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.campaigns() });
    },
  });
}

export function useUpdateMarketingCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, payload }) => adminService.updateMarketingCampaign(campaignId, payload),
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.campaign(campaignId) });
      queryClient.invalidateQueries({ queryKey: marketingKeys.campaigns() });
    },
  });
}

export function useSendMarketingCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (campaignId) => adminService.sendMarketingCampaign(campaignId),
    onSuccess: (_, campaignId) => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.campaign(campaignId) });
      queryClient.invalidateQueries({ queryKey: marketingKeys.campaigns() });
    },
  });
}

export function useTestSendMarketingCampaign() {
  return useMutation({
    mutationFn: ({ campaignId, email }) => adminService.testSendMarketingCampaign(campaignId, email),
  });
}

export function useMarketingSuppressionList(options = {}) {
  const search = options.search || '';
  const limit = options.limit ?? 200;
  return useQuery({
    queryKey: [...marketingKeys.suppression(search), limit],
    queryFn: () =>
      adminService.listMarketingSuppression({
        search: search || undefined,
        limit,
      }),
    staleTime: 30_000,
  });
}

export function useAddMarketingSuppression() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => adminService.addMarketingSuppression(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.suppression() });
    },
  });
}

export function useMarketingSuppressionCheck(email, enabled = true) {
  return useQuery({
    queryKey: [...marketingKeys.suppression(), 'check', email],
    queryFn: () => adminService.checkMarketingSuppression([email]),
    enabled: Boolean(email) && enabled,
    staleTime: 60_000,
  });
}

export function useCheckMarketingRecipients() {
  return useMutation({
    mutationFn: ({ campaignId, emails }) => adminService.checkMarketingRecipients(campaignId, emails),
  });
}

export function useGenerateMarketingHtmlBody() {
  return useMutation({
    mutationFn: ({ campaignId, payload }) => adminService.generateMarketingHtmlBody(campaignId, payload),
  });
}

export function useGenerateMarketingOpportunitiesPreview() {
  return useMutation({
    mutationFn: (payload) => adminService.generateMarketingOpportunitiesPreview(payload),
  });
}

export function useGenerateMarketingTemplateHtmlBody() {
  return useMutation({
    mutationFn: ({ templateId, payload }) => adminService.generateMarketingTemplateHtmlBody(templateId, payload),
  });
}

export function useEmailMarketingReport() {
  return useMutation({
    mutationFn: async (payload) => {
      const startRes = await adminService.emailMarketingReport(payload);
      const start = startRes?.data || startRes || {};
      const jobId = start.job_id;
      if (!jobId) {
        throw new Error('Campaign report did not start.');
      }
      const finalStatus = await pollJob(() => getJobStatus(jobId), {
        intervalMs: 1500,
        maxAttempts: 40, // ~1 min
      });
      if ((finalStatus.processing_status || finalStatus.status || '').toLowerCase() === 'failed') {
        throw new Error(finalStatus.error || finalStatus.progress_description || 'Unable to send campaign report.');
      }
      return finalStatus.result || {};
    },
  });
}

export function useEmailMarketingFollowUpReport() {
  return useMutation({
    mutationFn: (payload) => adminService.emailMarketingFollowUpReport(payload),
  });
}

export function useMarketingImportUploadUrl() {
  return useMutation({
    mutationFn: (payload) => adminService.getMarketingImportUploadUrl(payload),
  });
}

// ===== MARKETING ANALYTICS =====

export function useMarketingAnalytics(period = 'all_time', campaignIds = []) {
  const sortedIds = useMemo(
    () => (campaignIds?.length ? [...campaignIds].sort() : []),
    [campaignIds],
  );
  return useQuery({
    queryKey: marketingKeys.analytics(period, sortedIds),
    queryFn: () =>
      adminService.getMarketingAnalytics({
        period,
        ...(sortedIds.length ? { campaign_ids: sortedIds.join(',') } : {}),
      }),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useMarketingAnalyticsMetrics(period = 'all_time', campaignIds = [], enabled = true) {
  const sortedIds = useMemo(
    () => (campaignIds?.length ? [...campaignIds].sort() : []),
    [campaignIds],
  );
  return useQuery({
    queryKey: marketingKeys.analyticsMetrics(period, sortedIds),
    enabled: Boolean(enabled),
    queryFn: async ({ signal }) => {
      const startRes = await adminService.startMarketingAnalyticsMetrics({
        period,
        ...(sortedIds.length ? { campaign_ids: sortedIds.join(',') } : {}),
      });
      const start = startRes?.data || startRes || {};
      if (start.status === 'completed' && start.metrics) {
        return start.metrics;
      }
      const jobId = start.job_id;
      if (!jobId) {
        throw new Error('Metrics report did not start.');
      }
      const finalStatus = await pollJob(() => getJobStatus(jobId), {
        intervalMs: 2000,
        maxAttempts: 90,
        signal,
      });
      if ((finalStatus.processing_status || finalStatus.status || '').toLowerCase() === 'failed') {
        throw new Error(finalStatus.error || finalStatus.progress_description || 'Metrics report failed.');
      }
      const result = finalStatus.result || {};
      return result.metrics || result;
    },
    staleTime: 300_000,
    gcTime: 600_000,
    placeholderData: keepPreviousData,
  });
}

export function useMarketingAnalyticsRecipients(
  {
    period = 'all_time',
    campaignIds = [],
    engagementFilter,
    registerClickType,
    notSentType,
    enabled = true,
  } = {},
) {
  const sortedIds = useMemo(
    () => (campaignIds?.length ? [...campaignIds].sort() : []),
    [campaignIds],
  );
  const params = useMemo(
    () => ({
      period,
      engagement_filter: engagementFilter,
      ...(sortedIds.length ? { campaign_ids: sortedIds.join(',') } : {}),
      ...(registerClickType && registerClickType !== 'all'
        ? { register_click_type: registerClickType }
        : {}),
      ...(notSentType && notSentType !== 'all' ? { not_sent_type: notSentType } : {}),
    }),
    [period, sortedIds, engagementFilter, registerClickType, notSentType],
  );
  return useQuery({
    queryKey: marketingKeys.analyticsRecipients(params),
    queryFn: () => adminService.getMarketingAnalyticsRecipients(params),
    enabled: Boolean(enabled && engagementFilter),
    staleTime: 30_000,
  });
}

// ===== MARKETING CONTACTS =====

export function useMarketingContacts(params = {}, options = {}) {
  return useQuery({
    queryKey: marketingKeys.contacts(params),
    queryFn: () => adminService.listMarketingContacts(params),
    staleTime: 30_000,
    enabled: options.enabled !== false,
  });
}

export function useMarketingContact(id) {
  return useQuery({
    queryKey: marketingKeys.contact(id),
    queryFn: () => adminService.getMarketingContact(id),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateMarketingContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => adminService.createMarketingContact(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.contacts() });
    },
  });
}

export function useUpdateMarketingContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => adminService.updateMarketingContact(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.contact(id) });
      queryClient.invalidateQueries({ queryKey: marketingKeys.contacts() });
    },
  });
}

export function useDeleteMarketingContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => adminService.deleteMarketingContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.contacts() });
    },
  });
}

export function useParseMarketingAudience() {
  return useMutation({
    mutationFn: (payload) => adminService.parseMarketingAudience(payload),
  });
}

// Returns { job_id, status: processing } — poll useJobStatus until mapping_ready
export function useAnalyzeMarketingFile() {
  return useMutation({
    mutationFn: (payload) => adminService.analyzeMarketingFile(payload),
  });
}

// Returns { job_id } immediately — use useJobStatus to poll
export function useImportMarketingContacts() {
  return useMutation({
    mutationFn: (payload) => adminService.importMarketingContacts(payload),
  });
}

// Poll async job via universal manage_docs upload status endpoint.
export function useJobStatus(jobId, { onCompleted, isComplete } = {}) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [...marketingKeys.all, 'job-status', jobId],
    queryFn: () => getJobStatus(jobId),
    enabled: Boolean(jobId),
    refetchInterval: (query) => {
      const data = query.state.data;
      const status = data?.processing_status;
      if (status === 'completed' || status === 'failed') {
        if (status === 'completed' && (isComplete ? isComplete(data) : true)) {
          queryClient.invalidateQueries({ queryKey: marketingKeys.contacts() });
          onCompleted?.(data);
        }
        return false;
      }
      return 2000;
    },
    staleTime: 0,
  });
}

export function useConfirmMarketingContactsImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => adminService.confirmMarketingContactsImport(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.contacts() });
    },
  });
}

export function useBatchEnsureMarketingContacts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (contacts) => adminService.batchEnsureMarketingContacts(contacts),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.contacts() });
    },
  });
}

export function useContactCampaignHistory(id) {
  return useQuery({
    queryKey: marketingKeys.contactHistory(id),
    queryFn: () => adminService.getContactCampaignHistory(id),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

export function useContactTimeline(id) {
  return useQuery({
    queryKey: marketingKeys.contactTimeline(id),
    queryFn: () => adminService.getContactTimeline(id),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateContactActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, payload }) =>
      adminService.createContactActivity(contactId, payload),
    onSuccess: (_, { contactId }) => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.contact(contactId) });
      queryClient.invalidateQueries({ queryKey: marketingKeys.contactTimeline(contactId) });
      queryClient.invalidateQueries({ queryKey: marketingKeys.contacts() });
      queryClient.invalidateQueries({ queryKey: marketingKeys.contactFollowUps() });
    },
  });
}

export function useUpdateContactActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, activityId, payload }) =>
      adminService.updateContactActivity(contactId, activityId, payload),
    onSuccess: (_, { contactId }) => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.contact(contactId) });
      queryClient.invalidateQueries({ queryKey: marketingKeys.contactTimeline(contactId) });
      queryClient.invalidateQueries({ queryKey: marketingKeys.contacts() });
      queryClient.invalidateQueries({ queryKey: marketingKeys.contactFollowUps() });
    },
  });
}

export function useContactFollowUps(params = {}) {
  return useQuery({
    queryKey: marketingKeys.contactFollowUps(params),
    queryFn: () => adminService.listContactFollowUps(params),
    staleTime: 60_000,
  });
}

// ===== SALES CRM ACTION CENTER =====

export function useSalesActionCenter() {
  return useQuery({
    queryKey: marketingKeys.actionCenter(),
    queryFn: () => adminService.getSalesActionCenter(),
    staleTime: 30_000,
  });
}

// ===== SALES CRM ADMIN DIRECTORY (assign-to / @mentions) =====

export function useCrmAdminUsers() {
  return useQuery({
    queryKey: marketingKeys.adminUsers(),
    queryFn: () => adminService.listCrmAdminUsers(),
    staleTime: 5 * 60_000,
  });
}

// ===== SALES CRM SAVED VIEWS =====

export function useCrmSavedViews() {
  return useQuery({
    queryKey: marketingKeys.savedViews(),
    queryFn: () => adminService.listCrmSavedViews(),
    staleTime: 60_000,
  });
}

export function useSaveCrmSavedView() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => adminService.saveCrmSavedView(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.savedViews() });
    },
  });
}

export function useDeleteCrmSavedView() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (viewId) => adminService.deleteCrmSavedView(viewId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.savedViews() });
    },
  });
}

// ===== SALES CRM AUTOMATION (stall tasks + follow-up reminders) =====

export function useRunCrmAutomation() {
  return useMutation({
    mutationFn: () => adminService.runCrmAutomation(),
  });
}
